/* import __COLOCATED_TEMPLATE__ from './after-phone-call-ends.hbs'; */
/* RESPONSIBLE TEAM: team-ai-agent */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import type IntlService from 'embercom/services/intl';
import type Store from '@ember-data/store';
import { action } from '@ember/object';
import type FinVoiceConfigService from 'embercom/services/fin-voice-config-service';
import type FinVoiceNumberConfiguration from 'embercom/models/fin-voice/number-configuration';

interface Signature {
  Element: HTMLDivElement;
  Args: {
    accordion: any;
    ruleset: $TSFixMe;
    phoneNumber?: string;
    numberConfiguration?: FinVoiceNumberConfiguration;
    updateAfterPhoneCallEnds: (
      autoCloseWhenNotEscalated: boolean,
      defaultTeamId?: string,
    ) => Promise<void>;
  };
}

export default class AfterPhoneCallEnds extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare store: Store;
  @service declare finVoiceConfigService: FinVoiceConfigService;
  @service declare notificationsService: $TSFixMe;
  @service declare appService: $TSFixMe;

  @tracked autoCloseWhenNotEscalated = false;
  @tracked defaultTeamId?: string;

  constructor(owner: unknown, args: Signature['Args']) {
    super(owner, args);

    this.resetAfterPhoneCallEnds();
  }

  get hasChanges() {
    return (
      this.autoCloseWhenNotEscalated !== this.savedAutoCloseWhenNotEscalated ||
      this.defaultTeamId !== this.savedDefaultTeamId
    );
  }

  get savingDisabled() {
    return (
      !this.args.phoneNumber || Boolean(this.numberConfiguration?.isSaving) || !this.hasChanges
    );
  }

  get errorMessage() {
    if (!this.args.phoneNumber) {
      return this.intl.t(
        'operator.fin.setup.tabs.setup-and-go-live.phone.fin-behavior.no-phone-numbers',
      );
    }

    if (!this.hasChanges) {
      return this.intl.t(
        'operator.fin.setup.tabs.setup-and-go-live.phone.after-phone-call-ends.no-changes',
      );
    }

    return undefined;
  }

  get numberConfiguration() {
    return this.args.numberConfiguration;
  }

  get assignableAdmins() {
    return this.appService.app.assignableAdmins;
  }

  get savedAutoCloseWhenNotEscalated() {
    return this.numberConfiguration?.handoverConfiguration?.autoCloseWhenNotEscalated;
  }

  get savedDefaultTeamId() {
    return this.numberConfiguration?.handoverConfiguration?.defaultTeamId;
  }

  get autoCloseWhenNotEscalatedLabel() {
    if (this.autoCloseWhenNotEscalated) {
      return this.intl.t(
        'operator.fin.setup.tabs.setup-and-go-live.phone.after-phone-call-ends.auto-close-when-not-escalated',
      );
    }

    return this.intl.t(
      'operator.fin.setup.tabs.setup-and-go-live.phone.after-phone-call-ends.do-not-auto-close-when-not-escalated',
    );
  }

  @action
  resetAfterPhoneCallEnds() {
    this.autoCloseWhenNotEscalated = this.savedAutoCloseWhenNotEscalated ?? false;
    this.defaultTeamId = this.savedDefaultTeamId ?? undefined;
  }

  @action
  updateDefaultTeam(value: string) {
    this.defaultTeamId = value;
  }

  @action
  async updateAfterPhoneCallEnds() {
    if (!this.hasChanges) {
      return;
    }

    await this.args.updateAfterPhoneCallEnds(this.autoCloseWhenNotEscalated, this.defaultTeamId);
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Operator::Fin::Setup::Sections::NumberConfiguration::AfterPhoneCallEnds': typeof AfterPhoneCallEnds;
  }
}
